import { Form, Link, useRouteLoaderData } from "@remix-run/react";
import { type TFunction } from "i18next";
import { Check, Minus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClientOnly } from "remix-utils/client-only";
import { Select } from "#app/components/compat/antd";
import { Badge } from "#app/components/ui/badge.tsx";
import { Button } from "#app/components/ui/button.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "#app/components/ui/card-legacy.tsx";
import { formatCredits } from "#app/utils/stripe-ondemand.ts";

export type Plan = {
  id: string;
  planName: string;
  description: string | null;
  billingCycle: string;
  features: Record<string, unknown>;
  stripeMonthlyPrice?: number | null;
  stripeAnnualPrice?: number | null;
};

/** Active subscription per billing scope (`personal` or org name/id). */
export type UserActivePlan = {
  planId: string;
  billingCycle: string;
};

function normalizePricingBillingCycle(
  cycle: string | null | undefined,
): "monthly" | "annually" | null {
  if (!cycle) return null;
  const c = cycle.toLowerCase();
  if (c === "monthly" || c === "month") return "monthly";
  if (c === "annually" || c === "annual" || c === "yearly" || c === "year") {
    return "annually";
  }
  return null;
}

/**
 * Shared pricing section used on the landing page and the public pricing route.
 * When `plans` are passed, renders a custom card grid from the DB.
 * Otherwise falls back to the Stripe pricing table embed.
 */
export function PricingSection({
  id,
  className = "",
  plans,
  isLoggedIn = false,
  organizations = [],
  userActivePlans = {},
}: {
  id?: string;
  className?: string;
  plans?: Plan[];
  isLoggedIn?: boolean;
  organizations?: Array<{ id: string; name: string }>;
  userActivePlans?: Record<string, UserActivePlan>;
} = {}) {
  const { t } = useTranslation("marketing");
  const rootData = useRouteLoaderData("root") as {
    ENV?: {
      STRIPE_PUBLISHABLE_KEY?: string;
      STRIPE_PRICING_TABLE_ID?: string;
    };
  } | null;

  const env = rootData?.ENV;

  return (
    <section
      className={`w-full py-24 bg-linear-to-b from-transparent via-white to-white dark:via-zinc-950 dark:to-zinc-950 ${className}`.trim()}
      id={id ?? "pricing"}
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-semibold text-[#141A42] dark:text-zinc-100">
            {t("pricing.title")}
          </h2>
          <p className="mt-2 text-gray-500 text-lg dark:text-zinc-400">
            {t("pricing.subtitle")}
          </p>
        </div>

        {plans && plans.length > 0 ? (
          <PlanCards
            plans={plans}
            isLoggedIn={isLoggedIn}
            organizations={organizations}
            userActivePlans={userActivePlans}
            t={t}
          />
        ) : (
          <StripeFallback
            publishableKey={env?.STRIPE_PUBLISHABLE_KEY}
            pricingTableId={env?.STRIPE_PRICING_TABLE_ID}
          />
        )}
      </div>
    </section>
  );
}

// ─── Plan Cards ───────────────────────────────────────────────────────────────

const POPULAR_PLAN_ID = "pro";
const BUSINESS_PLAN_ID = "3";

function isBusinessPlan(plan: Plan) {
  return plan.id === BUSINESS_PLAN_ID || plan.planName === "Business";
}

function PlanCards({
  plans,
  isLoggedIn,
  organizations = [],
  userActivePlans = {},
  t,
}: {
  plans: Plan[];
  isLoggedIn: boolean;
  organizations?: Array<{ id: string; name: string }>;
  userActivePlans?: Record<string, UserActivePlan>;
  t: TFunction<"marketing">;
}) {
  const [billingScope, setBillingScope] = useState<"personal" | "org" | "">(
    organizations.length > 0 ? "" : "personal",
  );
  const [orgName, setOrgName] = useState(organizations[0]?.name ?? "");
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annually">(
    "monthly",
  );

  useEffect(() => {
    if (organizations.length === 0) return;
    if (!organizations.some((o) => o.name === orgName)) {
      setOrgName(organizations[0]!.name);
    }
  }, [organizations, orgName]);

  useEffect(() => {
    if (organizations.length === 0 && billingScope === "org") {
      setBillingScope("personal");
    }
  }, [organizations.length, billingScope]);

  const organizationOptions = useMemo(
    () =>
      organizations.map((org) => ({
        label: org.name,
        value: org.name,
      })),
    [organizations],
  );

  const showOrgBilling = isLoggedIn && organizations.length > 0;

  const activeScopeKey = billingScope === "personal" ? "personal" : orgName;
  const currentPlan = userActivePlans[activeScopeKey];
  const currentPlanId = currentPlan?.planId;
  const currentBillingCycle = normalizePricingBillingCycle(
    currentPlan?.billingCycle,
  );
  const isOnBusinessPlan = currentPlanId === BUSINESS_PLAN_ID;

  return (
    <div>
      <div className="mb-12 flex justify-center">
        <div className="inline-flex items-center rounded-full border border-gray-200 bg-white p-1 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
          <button
            type="button"
            onClick={() => setBillingCycle("monthly")}
            className={`rounded-full px-6 py-2 text-sm font-medium transition-colors ${
              billingCycle === "monthly"
                ? "bg-[#141A42] text-white dark:bg-zinc-100 dark:text-zinc-900"
                : "text-gray-500 hover:text-[#141A42] dark:text-zinc-400 dark:hover:text-zinc-100"
            }`}
          >
            {t("pricing.monthly")}
          </button>
          <button
            type="button"
            onClick={() => setBillingCycle("annually")}
            className={`rounded-full px-6 py-2 text-sm font-medium transition-colors ${
              billingCycle === "annually"
                ? "bg-[#141A42] text-white dark:bg-zinc-100 dark:text-zinc-900"
                : "text-gray-500 hover:text-[#141A42] dark:text-zinc-400 dark:hover:text-zinc-100"
            }`}
          >
            {t("pricing.annually")}
          </button>
        </div>
      </div>
      {showOrgBilling ? (
        <div className="mb-8 rounded-xl border border-gray-200 bg-white/80 p-4 text-left shadow-sm backdrop-blur-sm dark:border-zinc-700 dark:bg-zinc-900/80">
          <p className="text-sm font-semibold text-[#141A42] dark:text-zinc-100">
            {t("pricing.billingScopeTitle")}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
            {t("pricing.billingScopeHint")}
          </p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-[#141A42] dark:text-zinc-100">
              <input
                type="radio"
                className="h-4 w-4 accent-[#141A42]"
                name="pricing-billing-scope"
                checked={billingScope === "personal"}
                onChange={() => setBillingScope("personal")}
              />
              {t("pricing.personal")}
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-[#141A42] dark:text-zinc-100">
              <input
                type="radio"
                className="h-4 w-4 accent-[#141A42]"
                name="pricing-billing-scope"
                checked={billingScope === "org"}
                onChange={() => setBillingScope("org")}
              />
              {t("pricing.organization")}
            </label>
            {billingScope === "org" ? (
              <Select
                value={orgName || undefined}
                onChange={setOrgName}
                options={organizationOptions}
                className="w-full sm:min-w-[200px] sm:max-w-xs"
                placeholder={t("pricing.selectOrganization")}
              />
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {plans.map((plan) => {
          const isPopular = plan.id === POPULAR_PLAN_ID;
          const isEnterprise = isBusinessPlan(plan);
          const isFree =
            plan.id === "0" ||
            plan.stripeMonthlyPrice === 0 ||
            plan.features?.monthlyPriceUsd === 0;
          // Paid Basic monthly vs annual share the same plan id — only mark
          // "Current Plan" when the toggle matches the subscribed interval.
          const isCurrentPlan =
            plan.id === currentPlanId &&
            (isFree || currentBillingCycle === billingCycle);
          const features = formatFeatures(plan.features, t);
          const planKey = getPlanKey(plan.planName);
          const displayName = planKey
            ? t(`pricing.plans.${planKey}.name`)
            : plan.planName;
          const displayDesc =
            planKey && plan.description
              ? t(`pricing.plans.${planKey}.description`)
              : plan.description;

          const btnClass = `w-full ${
            isEnterprise
              ? "bg-white text-[#141A42] hover:bg-gray-100 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white"
              : isPopular
                ? "bg-[#141A42] text-white hover:bg-[#1e2a5e] dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white"
                : "border-[#141A42] text-[#141A42] hover:bg-[#141A42]/5 dark:border-zinc-300 dark:text-zinc-100 dark:hover:bg-zinc-800"
          }`;
          const btnVariant: "default" | "outline" =
            isPopular || isEnterprise ? "default" : "outline";

          return (
            <Card
              key={plan.id}
              className={`relative flex flex-col ${
                isPopular
                  ? "border-[#141A42] shadow-lg ring-2 ring-[#141A42]/20 dark:border-zinc-500 dark:bg-zinc-900 dark:ring-zinc-500/30"
                  : isEnterprise
                    ? "border-gray-200 bg-[#141A42] text-white dark:border-indigo-900"
                    : "border-gray-200 dark:border-zinc-700 dark:bg-zinc-900"
              }`}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-[#141A42] text-white text-xs px-3 py-0.5 rounded-full dark:border-indigo-800 dark:bg-indigo-950 dark:text-indigo-100">
                    {t("pricing.mostPopular")}
                  </Badge>
                </div>
              )}

              <CardHeader className="pb-4">
                <CardTitle
                  className={`text-lg font-semibold ${isEnterprise ? "text-white" : "text-[#141A42] dark:text-zinc-100"}`}
                >
                  {displayName}
                </CardTitle>
                {displayDesc && (
                  <CardDescription
                    className={`text-sm ${isEnterprise ? "text-gray-300" : "text-gray-500 dark:text-zinc-400"}`}
                  >
                    {displayDesc}
                  </CardDescription>
                )}
                <div className="mt-2">
                  <Badge
                    variant="outline"
                    className={`text-xs capitalize ${
                      isEnterprise
                        ? "border-gray-500 text-gray-300"
                        : "border-gray-300 text-gray-500 dark:border-zinc-600 dark:text-zinc-400"
                    }`}
                  >
                    {billingCycle === "monthly"
                      ? t("pricing.monthly")
                      : t("pricing.annually")}
                  </Badge>
                </div>
                {!isEnterprise &&
                  !isFree &&
                  (plan.stripeMonthlyPrice != null ||
                    plan.stripeAnnualPrice != null ||
                    plan.features.monthlyPriceUsd != null) && (
                    <div className="mt-4">
                      <div className="flex items-baseline gap-x-1">
                        <span
                          className={`text-3xl font-bold tracking-tight ${isEnterprise ? "text-white" : "text-[#141A42] dark:text-zinc-100"}`}
                        >
                          $
                          {billingCycle === "annually" && plan.stripeAnnualPrice
                            ? Math.round(plan.stripeAnnualPrice / 12)
                            : (plan.stripeMonthlyPrice ??
                              (plan.features.monthlyPriceUsd as number) ??
                              0)}
                        </span>
                        <span
                          className={`text-sm font-semibold ${isEnterprise ? "text-gray-300" : "text-gray-500 dark:text-zinc-400"}`}
                        >
                          {t("pricing.perMonth")}
                        </span>
                      </div>
                      {billingCycle === "annually" &&
                        plan.stripeAnnualPrice != null &&
                        plan.stripeMonthlyPrice != null && (
                          <p className="mt-1 text-xs text-green-600 dark:text-green-300 font-medium">
                            {t("pricing.billedYearly", {
                              amount: plan.stripeAnnualPrice,
                              savings:
                                plan.stripeMonthlyPrice! * 12 -
                                plan.stripeAnnualPrice!,
                            })}
                          </p>
                        )}
                    </div>
                  )}
                {isFree && (
                  <div className="mt-4">
                    <div className="flex items-baseline gap-x-1">
                      <span className="text-3xl font-bold tracking-tight text-[#141A42] dark:text-zinc-100">
                        $0
                      </span>
                      <span className="text-sm font-semibold text-gray-500 dark:text-zinc-400">
                        /month
                      </span>
                    </div>
                  </div>
                )}
                {isEnterprise && (
                  <div className="mt-4">
                    <span className="text-3xl font-bold tracking-tight text-white">
                      {t("pricing.custom")}
                    </span>
                  </div>
                )}
              </CardHeader>

              <CardContent className="flex-1">
                <ul className="space-y-2">
                  {features.map((feature) => (
                    <li
                      key={feature.label}
                      className="flex items-start gap-2 text-sm"
                    >
                      {feature.included ? (
                        <Check
                          className={`mt-0.5 h-4 w-4 shrink-0 ${
                            isEnterprise
                              ? "text-green-400"
                              : "text-green-500 dark:text-green-300"
                          }`}
                        />
                      ) : (
                        <Minus
                          className={`mt-0.5 h-4 w-4 shrink-0 ${
                            isEnterprise ? "text-gray-500" : "text-gray-400"
                          }`}
                        />
                      )}
                      <span
                        className={
                          isEnterprise
                            ? feature.included
                              ? "text-gray-200"
                              : "text-gray-500"
                            : feature.included
                              ? "text-gray-700 dark:text-zinc-300"
                              : "text-gray-400 dark:text-zinc-500"
                        }
                      >
                        {feature.label}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter className="pt-4">
                <PlanCTA
                  plan={plan}
                  isLoggedIn={isLoggedIn}
                  isFree={isFree}
                  isEnterprise={isEnterprise}
                  isCurrentPlan={isCurrentPlan}
                  isOnBusinessPlan={isOnBusinessPlan}
                  btnClass={btnClass}
                  btnVariant={btnVariant}
                  billingScope={billingScope}
                  organizationId={
                    billingScope === "org" && orgName ? orgName : ""
                  }
                  billingCycle={billingCycle}
                  showOrgBilling={showOrgBilling}
                  t={t}
                />
              </CardFooter>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function PlanCTA({
  plan,
  isLoggedIn,
  isFree,
  isEnterprise,
  isCurrentPlan,
  isOnBusinessPlan = false,
  btnClass,
  btnVariant,
  billingScope = "personal",
  organizationId = "",
  billingCycle,
  showOrgBilling,
  t,
}: {
  plan: Plan;
  isLoggedIn: boolean;
  isFree: boolean;
  isEnterprise: boolean;
  isCurrentPlan: boolean;
  isOnBusinessPlan?: boolean;
  btnClass: string;
  btnVariant: "default" | "outline";
  billingScope?: "personal" | "org" | "";
  organizationId?: string;
  billingCycle: "monthly" | "annually";
  showOrgBilling: boolean;
  t: TFunction<"marketing">;
}) {
  if (isCurrentPlan) {
    return (
      <Button disabled className={btnClass} variant={btnVariant}>
        {t("pricing.currentPlan")}
      </Button>
    );
  }

  if (isEnterprise) {
    return (
      <Button
        className={btnClass}
        variant={btnVariant}
        render={<Link to="/support" />}
      >
        {t("pricing.contactUs")}
      </Button>
    );
  }

  // Free plan — always allow navigation to dashboard
  if (isFree) {
    return (
      <Button
        className={btnClass}
        variant={btnVariant}
        render={
          <Link
            to={isLoggedIn ? "/u/dashboard" : "/login?redirectTo=/u/dashboard"}
          />
        }
      >
        {isLoggedIn ? t("pricing.goToDashboard") : t("pricing.getStartedFree")}
      </Button>
    );
  }

  if (isOnBusinessPlan) {
    return (
      <Button disabled className={btnClass} variant={btnVariant}>
        Get Started
      </Button>
    );
  }

  // Paid plan — logged in: POST to create checkout session
  if (isLoggedIn) {
    return (
      <Form
        method="post"
        action="/resources/stripe-checkout"
        className="w-full"
      >
        <input type="hidden" name="planId" value={plan.id} />
        <input type="hidden" name="billingScope" value={billingScope} />
        <input type="hidden" name="organizationId" value={organizationId} />
        <input type="hidden" name="billingCycle" value={billingCycle} />
        <Button
          type="submit"
          className={btnClass}
          variant={btnVariant}
          disabled={showOrgBilling && billingScope === ""}
        >
          {t("pricing.getStarted")}
        </Button>
      </Form>
    );
  }

  return (
    <Button
      className={btnClass}
      variant={btnVariant}
      render={<Link to="/login?redirectTo=/pricing" />}
    >
      {t("pricing.getStarted")}
    </Button>
  );
}

function getPlanKey(planName: string): string | null {
  const key = planName.toLowerCase();
  if (["starter", "basic", "pro", "business"].includes(key)) return key;
  return null;
}

type PlanFeatureRow = { id: string; label: string; included: boolean };

function formatFeatures(
  features: Record<string, unknown>,
  t: TFunction<"marketing">,
): PlanFeatureRow[] {
  const result: PlanFeatureRow[] = [];

  for (const [key, value] of Object.entries(features)) {
    if (
      key === "monthlyPriceUsd" ||
      key === "agents" ||
      key === "executions_per_month"
    ) {
      continue;
    }
    // Tag every row with its source feature key so ordering below can match on
    // the key rather than on translated copy.
    const push = (label: string, included: boolean) =>
      result.push({ id: key, label, included });

    if (key === "LLMTokens") {
      if (value === -1) {
        push(t("pricing.features.unlimitedLlmCredits"), true);
      } else {
        // Stored in tokens; users are allocated and billed in credits.
        push(
          t("pricing.features.llmCreditsPerMonth", {
            count: formatCredits(Number(value)),
          }),
          true,
        );
      }
      continue;
    }

    if (key === "parallel_executions") {
      if (value === 1) push(t("pricing.features.oneWorkflow"), true);
      else if (value === 5) push(t("pricing.features.upToFiveWorkflows"), true);
      else if (value === 10) push(t("pricing.features.tenPlusWorkflows"), true);
      else if (value === -1)
        push(t("pricing.features.workflowsByUseCase"), true);
      else push(t("pricing.features.nWorkflows", { count: value }), true);
      continue;
    }

    if (key === "team_members") {
      push(
        value === -1
          ? t("pricing.features.unlimitedMembers")
          : t("pricing.features.nMembers", { count: value }),
        true,
      );
      continue;
    }

    if (key === "integrations") {
      push(t("pricing.features.integrations"), value === true);
      continue;
    }
    if (key === "builder_community_access") {
      push(t("pricing.features.builderCommunity"), value === true);
      continue;
    }
    if (key === "audit_and_compliance") {
      push(t("pricing.features.auditCompliance"), value === true);
      continue;
    }
    if (key === "ai_execution_history") {
      if (value === "extended") {
        push(t("pricing.features.extendedHistory"), true);
      } else {
        push(t("pricing.features.executionHistory"), value === true);
      }
      continue;
    }
    if (key === "support") {
      if (value === false) {
        push(t("pricing.features.prioritySupport"), false);
      } else if (value === "priority") {
        push(t("pricing.features.prioritySupport"), true);
      } else if (value === "dedicated") {
        push(t("pricing.features.dedicatedSupport"), true);
      }
      continue;
    }
    if (key === "sso") {
      push(
        value === true
          ? t("pricing.features.ssoFull")
          : t("pricing.features.sso"),
        value === true,
      );
      continue;
    }
    if (key === "human_in_the_loop_controls") {
      push(t("pricing.features.humanInLoop", { value }), true);
      continue;
    }
    if (key === "usage_analytics") {
      push(t("pricing.features.usageAnalytics", { value }), true);
      continue;
    }

    if (value === true || value === false) {
      push(capitalize(key.replaceAll("_", " ")), value as boolean);
    } else if (typeof value === "string" || typeof value === "number") {
      push(`${capitalize(key.replaceAll("_", " "))}: ${value}`, true);
    }
  }

  // Reorder to put the credit allowance immediately after Active AI Workflows.
  // Matches on the feature key, not the rendered label -- the old label match
  // was locale-dependent and broke whenever the copy changed.
  const llmIndex = result.findIndex((r) => r.id === "LLMTokens");
  if (llmIndex > -1) {
    const llmItem = result.splice(llmIndex, 1)[0];
    if (llmItem) {
      const workflowsIndex = result.findIndex(
        (r) => r.id === "parallel_executions",
      );
      result.splice(workflowsIndex > -1 ? workflowsIndex + 1 : 0, 0, llmItem);
    }
  }

  return result;
}

function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// ─── Stripe fallback ──────────────────────────────────────────────────────────

function StripeFallback({
  publishableKey,
  pricingTableId,
}: {
  publishableKey?: string;
  pricingTableId?: string;
}) {
  const hasStripeConfig = Boolean(publishableKey && pricingTableId);

  if (!hasStripeConfig) {
    return (
      <div className="min-h-[200px] flex items-center justify-center text-gray-500">
        Pricing table not configured. Set STRIPE_PUBLISHABLE_KEY and
        STRIPE_PRICING_TABLE_ID in env.
      </div>
    );
  }

  return (
    <ClientOnly fallback={<div className="min-h-[200px]" />}>
      {() => (
        <div className="w-full justify-center">
          <stripe-pricing-table
            pricing-table-id={pricingTableId!}
            publishable-key={publishableKey!}
          />
        </div>
      )}
    </ClientOnly>
  );
}
